import { observer } from 'mobx-react-lite'
import { lazy } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ProfileOutlined } from '@ant-design/icons'

import { useStore } from 'store/useStore'
import { ContainerLK } from 'sections/LKSection/shared/components/ContainerLK/ContainerLK'
import { Status, StatusUIType } from 'sections/LKSection/shared/components/UI/Status/Status'
import { Margin, Padding } from 'components/ui'
import { Button, Flex, Pagination, Skeleton, Title } from 'components/uiKit'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
import { sendEvent } from 'utils/helpers/sendEvent'
import { useAsyncEffect } from 'utils/hooks/useAsyncEffect'
import { useResize } from 'utils/hooks/useResize'
import { LKRoutesEnum } from 'shared/constants/routes'
import { EventType } from 'types/types'

import { OrderItemHeader } from './components/OrderItemHeader/OrderItemHeader'
import { OrderKeys } from './data'

import s from './OrderPage.module.scss'

const OrderPage = observer(() => {
  const { viewOrderUiStore: uiStore, createOrderUiStore } = useStore()

  const navigate = useNavigate()
  const { t } = useTranslation()
  const { isTablet, isMobile } = useResize()

  const showAdditional = uiStore.orderStore.totalCount > 20

  useAsyncEffect(async () => {
    if (uiStore.orderList.length === 0) {
      await uiStore.getOrderList()
    }
  }, [])

  const getKey = (key: string) => `${OrderKeys.orderPage}.${key}`

  const paginationChange = (page: number, pageSize: number) => {
    if (pageSize !== uiStore.pageSize && uiStore.page !== 1) {
      uiStore.setPage(1)
    } else {
      uiStore.setPage(page)
    }

    uiStore.setPageSize(pageSize)

    void uiStore.getOrderList()
  }

  const createOrderHandler = () => {
    sendEvent(EventType.createOrder)
    createOrderUiStore.setSelectedStep(1)
    navigate(LKRoutesEnum.orderCreate)
  }

  return (
    <ContainerLK>
      <Padding py={32} className={s.OrderPage}>
        <Flex justify="space-between" align={isMobile ? 'unset' : 'center'} vertical={isMobile} gap={16}>
          <Title>{t(getKey('Заявки на открытие ПВЗ'))}</Title>
          {!showAdditional && <Button onClick={createOrderHandler}>{t(getKey('Создать заявку'))}</Button>}
        </Flex>

        <Margin mb={32} mt={20}>
          {showAdditional && (
            <Flex
              justify="space-between"
              vertical={isTablet}
              gap={16}
              style={{ flexDirection: isTablet ? 'column-reverse' : 'unset' }}
            >
              <Flex gap={16} vertical={isMobile} style={{ flexDirection: isTablet ? 'column-reverse' : 'unset' }}>
                <Pagination
                  current={uiStore.page}
                  onChange={paginationChange}
                  pageSize={uiStore.pageSize}
                  total={uiStore.orderStore.totalCount}
                  responsive
                />
              </Flex>

              <Button onClick={createOrderHandler}>{t(getKey('Создать заявку'))}</Button>
            </Flex>
          )}
        </Margin>

        <Skeleton loading={uiStore.loadingOrderList}>
          {uiStore.orderList.length > 0 ? (
            <Flex vertical gap={8}>
              {showAdditional && (
                <Flex>
                  <Status
                    title={`${t(getSharedKey('amount'))}: ${uiStore.orderStore.totalCount}`}
                    type={StatusUIType.gray}
                    smallSize
                  />
                </Flex>
              )}

              {uiStore.orderList.map((order) => (
                <OrderItemHeader key={order.id} order={order} />
              ))}
            </Flex>
          ) : (
            <Flex className={s.emptyBlock} vertical justify="center" align="center" gap={8}>
              <ProfileOutlined className={s.icon} />

              <Title type="secondary" level={4}>
                {t(getKey('Нет заявок'))}
              </Title>
            </Flex>
          )}
        </Skeleton>
      </Padding>
    </ContainerLK>
  )
})

export default OrderPage

export const OrderPageAsync = lazy(() => import('./OrderPage'))
